import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../api';
import {
  Icon, Modal, Pager, Switch, TunPicker, fmtIso, fmtNum, todayKey, useToast,
} from '../components/ui';

const DAY_LABELS = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา']; // index 0 = Monday (ตาม backend)
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const MAX_LISTED_TUNS = 6;

const STATUS_META = {
  sent: { label: 'ส่งสำเร็จ', cls: 'ok' },
  sent_delete_error: { label: 'ส่งแล้ว แต่ลบไฟล์ไม่สำเร็จ', cls: 'wait' },
  error: { label: 'ส่งไม่สำเร็จ', cls: 'err' },
  skipped: { label: 'ไม่มีไฟล์', cls: 'mute' },
};

const FREQUENCY_LABELS = {
  weekly: 'รายสัปดาห์ (เลือกวัน)',
  every_n_days: 'ทุก N วัน',
  monthly: 'รายเดือน',
  once: 'ครั้งเดียว',
};

function emptyForm() {
  return {
    id: null, name: '', all_tuns: false, tuns: [],
    frequency: 'weekly', days: [...ALL_DAYS],
    interval_days: 1, start_date: todayKey(), end_date: '',
    day_of_month: 1, once_date: todayKey(),
    time_mode: 'fixed', times: ['08:00'],
    interval_minutes: 60, interval_start: '08:00', interval_end: '17:00',
    email_override: '', bcc: '', subject: '', message: '', enabled: true,
  };
}

function scopeLabel(s) {
  if (s.all_tuns) return 'ทุก TUN';
  if (!s.tuns || !s.tuns.length) return '(ไม่ได้เลือก)';
  const shown = s.tuns.slice(0, MAX_LISTED_TUNS).join(', ');
  return s.tuns.length > MAX_LISTED_TUNS ? `${shown} และอีก ${s.tuns.length - MAX_LISTED_TUNS}` : shown;
}

function frequencyLabel(s) {
  const freq = s.frequency || 'weekly';
  const until = s.end_date ? ` ถึง ${s.end_date}` : '';
  if (freq === 'weekly') {
    const days = !s.days || s.days.length === 7 ? 'ทุกวัน' : s.days.slice().sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(' ');
    return days + until;
  }
  if (freq === 'every_n_days') return `ทุก ${s.interval_days} วัน เริ่ม ${s.start_date}${until}`;
  if (freq === 'monthly') return `วันที่ ${s.day_of_month} ของเดือน${until}`;
  if (freq === 'once') return `ครั้งเดียว ${s.once_date}`;
  return freq;
}

function Times({ s }) {
  if (s.time_mode === 'interval') return <span className="code">ทุก {s.interval_minutes} นาที {s.interval_start}–{s.interval_end}</span>;
  if (!s.times || !s.times.length) return <span className="muted">—</span>;
  return <div className="codes">{s.times.map((t) => <span key={t} className="code">{t}</span>)}</div>;
}

export default function Schedule() {
  const toast = useToast();
  const [allTuns, setAllTuns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState(null); // null = ปิดฟอร์ม
  const [timeInput, setTimeInput] = useState('08:00');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [runTarget, setRunTarget] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try { setSchedules((await apiGet('/api/schedule/list')).schedules || []); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => { try { setAllTuns((await apiGet('/api/files/tuns')).tuns || []); } catch (e) { /* ignore */ } })();
    loadSchedules();
  }, [loadSchedules]);

  // ── ประวัติการส่ง ──
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [fSchedule, setFSchedule] = useState('');
  const [fTun, setFTun] = useState([]);
  const [fStatus, setFStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(rowsPerPage), offset: String(page * rowsPerPage) });
      if (fSchedule) params.set('schedule_id', fSchedule);
      if (fTun[0]) params.set('tun', fTun[0]);
      if (fStatus) params.set('status', fStatus);
      const data = await apiGet(`/api/schedule/logs?${params.toString()}`);
      setLogs(data.entries || []);
      setLogsTotal(data.total || 0);
    } finally {
      setLogsLoading(false);
    }
  }, [fSchedule, fTun, fStatus, page, rowsPerPage]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openCreate = () => { setForm(emptyForm()); setTimeInput('08:00'); setFormError(''); };
  const openEdit = (s) => { setForm({ ...emptyForm(), ...s }); setTimeInput('08:00'); setFormError(''); };
  const toggleDay = (d) => setForm((f) => ({
    ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort((a, b) => a - b),
  }));
  const addTime = () => { if (timeInput) setForm((f) => (f.times.includes(timeInput) ? f : { ...f, times: [...f.times, timeInput].sort() })); };
  const removeTime = (t) => setForm((f) => ({ ...f, times: f.times.filter((x) => x !== t) }));

  const saveForm = async () => {
    setSaving(true);
    setFormError('');
    try {
      const data = await apiPost(form.id ? '/api/schedule/update' : '/api/schedule/create', form);
      if (!data.ok) { setFormError(data.error || 'บันทึกไม่สำเร็จ'); return; }
      toast(form.id ? 'บันทึกตารางส่งแล้ว' : 'เพิ่มตารางส่งแล้ว');
      setForm(null);
      loadSchedules();
    } catch (e) {
      setFormError('บันทึกไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (s) => {
    try {
      await apiPost('/api/schedule/update', { id: s.id, enabled: !s.enabled });
      toast(`${s.enabled ? 'ปิด' : 'เปิด'}ตารางส่ง "${s.name || 'ไม่มีชื่อ'}" แล้ว`);
    } catch (e) { toast('เปลี่ยนสถานะไม่สำเร็จ', 'error'); }
    loadSchedules();
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await apiPost('/api/schedule/delete', { id: deleteTarget.id });
      toast('ลบตารางส่งแล้ว');
      setDeleteTarget(null);
      loadSchedules();
    } catch (e) { toast('ลบไม่สำเร็จ', 'error'); } finally { setDeleting(false); }
  };

  const confirmRunNow = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const data = await apiPost('/api/schedule/run-now', { id: runTarget.id });
      setRunResult(data.ok ? { ok: true, msg: data.result.summary } : { ok: false, msg: data.error || 'รันไม่สำเร็จ' });
      loadSchedules();
      loadLogs();
    } catch (e) {
      setRunResult({ ok: false, msg: 'รันไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้' });
    } finally {
      setRunning(false);
    }
  };

  const closeRun = () => { if (!running) { setRunTarget(null); setRunResult(null); } };

  return (
    <>
      <div className="page-head">
        <div className="page-title"><h1>ตารางส่ง</h1><span className="meta">ส่งไฟล์ใน Output ให้ลูกค้าทางอีเมลตามเวลา</span></div>
        <div className="page-actions"><button className="btn btn-primary" onClick={openCreate}><Icon name="plus" />เพิ่มตารางส่ง</button></div>
      </div>

      <section className="card">
        <div className="callout">เมื่อส่งสำเร็จ ระบบ<b>ลบไฟล์ต้นฉบับใน Output ทิ้งทันที</b> ถ้าส่งไม่สำเร็จไฟล์จะยังอยู่ และรอบถัดไปจะส่งใหม่</div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>ชื่อ</th><th>ขอบเขต</th><th>รอบ</th><th>เวลา</th><th>เปิดใช้</th><th>รันล่าสุด</th><th className="r" aria-label="คำสั่ง" /></tr></thead>
            <tbody>
              {loading && !schedules.length ? (
                <tr><td colSpan={7} className="empty">กำลังโหลด…</td></tr>
              ) : !schedules.length ? (
                <tr><td colSpan={7} className="empty">ยังไม่มีตารางส่ง กด "เพิ่มตารางส่ง" เพื่อสร้าง</td></tr>
              ) : schedules.map((s) => (
                <tr key={s.id}>
                  <td className="strong">{s.name || <span className="muted">(ไม่มีชื่อ)</span>}</td>
                  <td>{s.all_tuns ? 'ทุก TUN' : <span className="mono" style={{ fontSize: 12.5 }}>{scopeLabel(s)}</span>}</td>
                  <td>{frequencyLabel(s)}</td>
                  <td><Times s={s} /></td>
                  <td><Switch checked={!!s.enabled} onChange={() => toggleEnabled(s)} label={`เปิดใช้ ${s.name || ''}`} /></td>
                  <td>{s.last_run_tick ? <span title={s.last_run_summary || ''}>{s.last_run_tick}</span> : <span className="muted">ยังไม่เคยรัน</span>}</td>
                  <td>
                    <div className="row-actions">
                      <button className="row-act" title="รันเดี๋ยวนี้" aria-label="รันเดี๋ยวนี้" onClick={() => { setRunResult(null); setRunTarget(s); }}><Icon name="play" /></button>
                      <button className="row-act" title="แก้ไข" aria-label="แก้ไข" onClick={() => openEdit(s)}><Icon name="edit" /></button>
                      <button className="row-act danger" title="ลบ" aria-label="ลบ" onClick={() => setDeleteTarget(s)}><Icon name="trash" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h3>ประวัติการส่ง</h3>
          <button className="btn btn-outline btn-sm" onClick={loadLogs} disabled={logsLoading}><Icon name="refresh" />รีเฟรช</button>
        </div>
        <div className="toolbar">
          <select className="input" value={fSchedule} onChange={(e) => { setPage(0); setFSchedule(e.target.value); }} aria-label="กรองตามตารางส่ง">
            <option value="">ทุกตารางส่ง</option>
            {schedules.map((s) => <option key={s.id} value={s.id}>{s.name || '(ไม่มีชื่อ)'}</option>)}
          </select>
          <select className="input" value={fStatus} onChange={(e) => { setPage(0); setFStatus(e.target.value); }} aria-label="กรองตามสถานะ">
            <option value="">ทุกสถานะ</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div style={{ minWidth: 200, flex: '0 1 240px' }}>
            <TunPicker options={allTuns} value={fTun} onChange={(v) => { setPage(0); setFTun(v.slice(-1)); }} placeholder="กรองตาม TUN" />
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>เวลา</th><th>ตารางส่ง</th><th>TUN</th><th>สถานะ</th><th className="r">ไฟล์</th><th>ผู้รับ</th><th>หมายเหตุ</th></tr></thead>
            <tbody>
              {logsLoading && !logs.length ? (
                <tr><td colSpan={7} className="empty">กำลังโหลด…</td></tr>
              ) : !logs.length ? (
                <tr><td colSpan={7} className="empty">ยังไม่มีประวัติการส่ง</td></tr>
              ) : logs.map((e) => {
                const meta = STATUS_META[e.status] || { label: e.status, cls: 'mute' };
                return (
                  <tr key={e.id}>
                    <td className="num nowrap">{fmtIso(e.timestamp)}</td>
                    <td>{e.schedule_name || <span className="muted">(ไม่มีชื่อ)</span>}</td>
                    <td><span className="code">{e.tun}</span></td>
                    <td><span className={`st ${meta.cls}`}>● {meta.label}</span></td>
                    <td className="r num">{e.file_count}</td>
                    <td>{e.recipient || <span className="muted">—</span>}</td>
                    <td>{e.error ? <span className="file" style={{ color: 'var(--danger)', maxWidth: 320 }} title={e.error}>{e.error}</span> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="foot">
          <span className="row">
            {logsTotal ? `แสดง ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, logsTotal)} จาก ${fmtNum(logsTotal)}` : 'ไม่มีรายการ'}
            <select className="input" style={{ padding: '3px 6px', fontSize: 13 }} value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} aria-label="จำนวนต่อหน้า">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / หน้า</option>)}
            </select>
          </span>
          <Pager page={page} pages={Math.ceil(logsTotal / rowsPerPage)} onPage={setPage} />
        </div>
      </section>

      {/* ── ฟอร์มเพิ่ม/แก้ไข ── */}
      <Modal open={!!form} wide title={form?.id ? 'แก้ไขตารางส่ง' : 'เพิ่มตารางส่ง'} onClose={() => setForm(null)} busy={saving}
        actions={(
          <>
            <button className="btn btn-outline" onClick={() => setForm(null)} disabled={saving}>ยกเลิก</button>
            <button className="btn btn-primary" onClick={saveForm} disabled={saving}>{saving ? 'กำลังบันทึก…' : 'บันทึก'}</button>
          </>
        )}>
        {form && (
          <>
            <div className="field">
              <label className="lbl" htmlFor="sc-name">ชื่อ</label>
              <input className="input" id="sc-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="เช่น PDF เช้า" />
            </div>
            <div className="field">
              <label className="check-label"><input type="checkbox" checked={form.all_tuns} onChange={(e) => set('all_tuns', e.target.checked)} />ทุก TUN</label>
              {!form.all_tuns && <div style={{ marginTop: 8 }}><TunPicker options={allTuns} value={form.tuns} onChange={(v) => set('tuns', v)} /></div>}
            </div>

            <hr className="divider" />
            <div className="field">
              <label className="lbl" htmlFor="sc-freq">รอบ</label>
              <select className="input" id="sc-freq" style={{ width: '100%' }} value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
                {Object.entries(FREQUENCY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            {form.frequency === 'weekly' && (
              <div className="field days">
                {DAY_LABELS.map((label, d) => (
                  <button key={d} type="button" className={`chipbtn${form.days.includes(d) ? ' on' : ''}`} onClick={() => toggleDay(d)}>{label}</button>
                ))}
              </div>
            )}
            {form.frequency === 'every_n_days' && (
              <div className="form-grid field">
                <div><label className="lbl" htmlFor="sc-n">ทุกกี่วัน</label><input className="input" id="sc-n" type="number" min={1} value={form.interval_days} onChange={(e) => set('interval_days', parseInt(e.target.value, 10) || 1)} /></div>
                <div><label className="lbl" htmlFor="sc-start">เริ่มวันที่</label><input className="input" id="sc-start" type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} /></div>
              </div>
            )}
            {form.frequency === 'monthly' && (
              <div className="field">
                <label className="lbl" htmlFor="sc-dom">วันที่ของเดือน</label>
                <input className="input" id="sc-dom" type="number" min={1} max={31} style={{ width: 120 }} value={form.day_of_month} onChange={(e) => set('day_of_month', parseInt(e.target.value, 10) || 1)} />
                <div className="hint">เดือนที่สั้นกว่าจะใช้วันสุดท้ายของเดือนแทน</div>
              </div>
            )}
            {form.frequency === 'once' && (
              <div className="field">
                <label className="lbl" htmlFor="sc-once">วันที่</label>
                <input className="input" id="sc-once" type="date" style={{ width: 'auto' }} value={form.once_date} onChange={(e) => set('once_date', e.target.value)} />
                <div className="hint">รันแล้วจะปิดตัวเองอัตโนมัติ</div>
              </div>
            )}
            {form.frequency !== 'once' && (
              <div className="field">
                <label className="lbl" htmlFor="sc-end">สิ้นสุดวันที่ (ไม่บังคับ)</label>
                <input className="input" id="sc-end" type="date" style={{ width: 'auto' }} value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
                <div className="hint">เว้นว่าง = ทำซ้ำไปเรื่อย ๆ · เลยวันนี้แล้วจะปิดตัวเองอัตโนมัติ</div>
              </div>
            )}

            <hr className="divider" />
            <div className="field">
              <label className="lbl" htmlFor="sc-tm">เวลา</label>
              <select className="input" id="sc-tm" style={{ width: '100%' }} value={form.time_mode} onChange={(e) => set('time_mode', e.target.value)}>
                <option value="fixed">ระบุเวลา</option>
                <option value="interval">ทุก N นาที ในช่วงเวลา</option>
              </select>
            </div>
            {form.time_mode === 'fixed' ? (
              <div className="field">
                <div className="codes" style={{ marginBottom: 8 }}>
                  {form.times.map((t) => (
                    <span key={t} className="tunpick"><span className="t">{t}<button type="button" aria-label={`ลบเวลา ${t}`} onClick={() => removeTime(t)}>×</button></span></span>
                  ))}
                  {!form.times.length && <span className="muted" style={{ fontSize: 13 }}>ยังไม่มีเวลา</span>}
                </div>
                <div className="row">
                  <input className="input" type="time" style={{ width: 'auto' }} value={timeInput} onChange={(e) => setTimeInput(e.target.value)} aria-label="เวลาที่จะเพิ่ม" />
                  <button type="button" className="btn btn-outline btn-sm" onClick={addTime}><Icon name="plus" />เพิ่มเวลา</button>
                </div>
              </div>
            ) : (
              <div className="form-grid field" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                <div><label className="lbl" htmlFor="sc-im">ทุกกี่นาที</label><input className="input" id="sc-im" type="number" min={1} value={form.interval_minutes} onChange={(e) => set('interval_minutes', parseInt(e.target.value, 10) || 1)} /></div>
                <div><label className="lbl" htmlFor="sc-is">ตั้งแต่</label><input className="input" id="sc-is" type="time" value={form.interval_start} onChange={(e) => set('interval_start', e.target.value)} /></div>
                <div><label className="lbl" htmlFor="sc-ie">ถึง</label><input className="input" id="sc-ie" type="time" value={form.interval_end} onChange={(e) => set('interval_end', e.target.value)} /></div>
              </div>
            )}

            <hr className="divider" />
            <div className="form-grid">
              <div className="full">
                <label className="lbl" htmlFor="sc-to">ส่งถึง (ไม่บังคับ)</label>
                <input className="input" id="sc-to" value={form.email_override} onChange={(e) => set('email_override', e.target.value)} />
                <div className="hint">เว้นว่าง = ใช้อีเมล Contact ของแต่ละ TUN จากข้อมูลลูกค้า</div>
              </div>
              <div className="full"><label className="lbl" htmlFor="sc-bcc">สำเนาลับ (Bcc)</label><input className="input" id="sc-bcc" value={form.bcc} onChange={(e) => set('bcc', e.target.value)} placeholder="ไม่บังคับ คั่นด้วยจุลภาค" /></div>
              <div className="full"><label className="lbl" htmlFor="sc-sub">หัวเรื่อง</label><input className="input" id="sc-sub" value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="Order documents" /></div>
              <div className="full"><label className="lbl" htmlFor="sc-msg">ข้อความ</label><textarea className="input" id="sc-msg" rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Please find the attached order documents." /></div>
            </div>
            <div className="switch-row" style={{ marginTop: 14 }}>
              <span className="t">{form.enabled ? 'เปิดใช้' : 'ปิดอยู่'}</span>
              <Switch checked={form.enabled} onChange={(v) => set('enabled', v)} label="เปิดใช้ตารางส่งนี้" />
            </div>
            <div className="alert alert-warn" style={{ marginTop: 14 }}>ส่งสำเร็จแล้วไฟล์จะถูกลบจาก Output ทันที เรียกคืนไม่ได้</div>
            {formError && <div className="alert alert-error" style={{ marginTop: 10 }}>{formError}</div>}
          </>
        )}
      </Modal>

      <Modal open={!!deleteTarget} title="ลบตารางส่ง" onClose={() => setDeleteTarget(null)} busy={deleting}
        actions={(
          <>
            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>ยกเลิก</button>
            <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>{deleting ? 'กำลังลบ…' : 'ลบ'}</button>
          </>
        )}>
        <p>ลบตารางส่ง <b>{deleteTarget?.name || '(ไม่มีชื่อ)'}</b> ลบแค่ตารางนี้ ไม่กระทบไฟล์ใด ๆ</p>
      </Modal>

      <Modal open={!!runTarget} title="รันตารางส่งเดี๋ยวนี้" onClose={closeRun} busy={running}
        actions={runResult ? (
          <button className="btn btn-outline" onClick={closeRun}>ปิด</button>
        ) : (
          <>
            <button className="btn btn-outline" onClick={closeRun} disabled={running}>ยกเลิก</button>
            <button className="btn btn-primary" onClick={confirmRunNow} disabled={running}>{running ? 'กำลังส่ง…' : 'รันเดี๋ยวนี้'}</button>
          </>
        )}>
        {!runResult ? (
          <p>ส่งไฟล์ใน Output ของ <b>{runTarget ? scopeLabel(runTarget) : ''}</b> ทางอีเมลทันที และ<b style={{ color: 'var(--danger)' }}>ลบไฟล์เหล่านั้น</b>เมื่อส่งสำเร็จ</p>
        ) : (
          <div className={`alert ${runResult.ok ? 'alert-ok' : 'alert-error'}`}>{runResult.msg}</div>
        )}
      </Modal>
    </>
  );
}
